import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDomain } from '../lib/api';
import { DomainHealthHeader } from '../components/dashboard/DomainHealthHeader';
import { SavingsByCategory } from '../components/dashboard/SavingsByCategory';
import { CostOptimizationStatus } from '../components/dashboard/CostOptimizationStatus';
import { TopOpportunities } from '../components/dashboard/TopOpportunities';
import { DomainTrends } from '../components/dashboard/DomainTrends';
import { DomainSyncHealth } from '../components/dashboard/DomainSyncHealth';
import { DomainTopSavers } from '../components/dashboard/DomainTopSavers';
import { InviteTeamNudge } from '../components/dashboard/InviteTeamNudge';
import { DomainCompliancePosture } from '../components/dashboard/CompliancePosture';

/**
 * Domain dashboard — the default landing tab for a domain (DASH at env
 * scope). Engineering work-queue oriented: health + spend + recommendation
 * pipeline at a glance, the highest-impact open recommendations to act on,
 * 30-day trends, the realized-savings leaderboard (when enabled), and connection
 * + sync health. Everything deep-links into the deeper tabs.
 */
export function DomainDashboard() {
  const { domainId = '' } = useParams();
  const env = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => getDomain(domainId),
    enabled: !!domainId,
  });
  const showLeaderboard = env.data?.showLeaderboard ?? true;

  return (
    <div className="space-y-6">
      <DomainHealthHeader domainId={domainId} />
      <InviteTeamNudge domainId={domainId} />
      <SavingsByCategory scope="env" domainId={domainId} />
      {/* The optimizations to act on come first — before the lifecycle funnel —
          so the highest-impact work is visible in the first view. */}
      <TopOpportunities domainId={domainId} />
      <CostOptimizationStatus scope="env" domainId={domainId} />
      <DomainCompliancePosture domainId={domainId} />
      <DomainTrends domainId={domainId} />
      {showLeaderboard ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <DomainTopSavers domainId={domainId} />
          <DomainSyncHealth domainId={domainId} />
        </div>
      ) : (
        <DomainSyncHealth domainId={domainId} />
      )}
    </div>
  );
}
